/**
 * Havenwear theme.js
 * Vanilla ES2020, no dependencies. Every interactive component is a custom
 * element so it initialises itself when rendered, including Section Rendering
 * API responses and Theme Editor reloads.
 */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  theme.strings = theme.strings || {};
  theme.routes = theme.routes || {};
  theme.cart = theme.cart || { type: 'drawer' };
  theme.freeShipping = theme.freeShipping || {};
  theme.overlays = [];

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* ---------------------------------------------------------------------
     Utilities
     --------------------------------------------------------------------- */
  theme.utils = {
    $,
    $$,
    define,
    debounce(fn, wait = 250) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
      };
    },
    onFrame(fn) {
      let queued = false;
      return (...args) => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          fn(...args);
        });
      };
    },
    parseHTML(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    },
    reducedMotion() {
      return (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        document.documentElement.classList.contains('reduce-motion')
      );
    },
    storage: {
      get(key) {
        try {
          return JSON.parse(window.localStorage.getItem(key));
        } catch (error) {
          return null;
        }
      },
      set(key, value) {
        try {
          window.localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
          /* Storage can be unavailable in private modes. */
        }
      },
    },
    session: {
      get(key) {
        try {
          return window.sessionStorage.getItem(key);
        } catch (error) {
          return null;
        }
      },
      set(key, value) {
        try {
          window.sessionStorage.setItem(key, value);
        } catch (error) {
          /* Ignore. */
        }
      },
    },
  };

  /* Money formatting that honours the shop's money_format placeholders. */
  theme.formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    const value = typeof cents === 'string' ? parseInt(cents.replace(/[^\d-]/g, ''), 10) : Math.round(cents);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number === null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const match = format.match(placeholder);
    if (!match) return format;
    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = withDelimiters(value, 0, ' ');
        break;
      case 'amount_with_space_separator':
        formatted = withDelimiters(value, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        formatted = withDelimiters(value, 2, ' ', '.');
        break;
      default:
        formatted = withDelimiters(value, 2);
    }
    return format.replace(placeholder, formatted);
  };

  theme.formatMoneyShort = (cents) => theme.formatMoney(cents).replace(/([.,])00(?!\d)/, '');

  theme.currencyRate = () => parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;

  theme.toast = (message) => {
    const region = $('[data-toast-region]');
    if (!region || !message) return;
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    region.appendChild(toast);
    window.setTimeout(() => toast.remove(), 2900);
  };

  /* ---------------------------------------------------------------------
     Scroll lock + focus management
     --------------------------------------------------------------------- */
  let lockCount = 0;
  theme.scrollLock = {
    lock() {
      lockCount += 1;
      document.documentElement.classList.add('scroll-lock');
    },
    unlock() {
      lockCount = Math.max(0, lockCount - 1);
      if (!lockCount) document.documentElement.classList.remove('scroll-lock');
    },
  };

  const FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  theme.a11y = {
    handler: null,
    focusable(container) {
      return $$(FOCUSABLE, container).filter((el) => el.getClientRects().length > 0 && !el.closest('[inert]'));
    },
    trapFocus(container, focusTarget) {
      this.releaseFocus();
      this.handler = (event) => {
        if (event.key !== 'Tab') return;
        const items = this.focusable(container);
        if (!items.length) {
          event.preventDefault();
          container.focus();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !container.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !container.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this.handler);
      if (focusTarget !== false) (focusTarget || container).focus({ preventScroll: true });
    },
    releaseFocus() {
      if (this.handler) document.removeEventListener('keydown', this.handler);
      this.handler = null;
    },
  };

  /* Focus can silently fail while an overlay is still becoming visible, so retry once. */
  theme.a11y.focusInto = (container, target) => {
    theme.a11y.trapFocus(container, target);
    window.setTimeout(() => {
      if (!container.contains(document.activeElement)) (target || container).focus({ preventScroll: true });
    }, 350);
  };

  /* ---------------------------------------------------------------------
     Overlay base: drawers, modals and popups share open/close, ESC,
     focus trapping, scroll lock and focus restoration.
     --------------------------------------------------------------------- */
  class OverlayElement extends HTMLElement {
    constructor() {
      super();
      this.dismissible = true;
    }

    connectedCallback() {
      if (this.overlayReady) return;
      this.overlayReady = true;
      this.panel = this.querySelector('[role="dialog"], [role="alertdialog"]') || this;
      this.addEventListener('click', (event) => {
        if (!this.dismissible) return;
        if (event.target.closest('[data-drawer-close], [data-modal-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen && this.dismissible) {
          event.stopPropagation();
          this.close();
        }
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    focusTarget() {
      return this.panel;
    }

    setExpanded(state) {
      const opener = this.opener;
      if (opener && opener.getAttribute && opener.getAttribute('aria-controls') === this.id && opener.hasAttribute('aria-expanded')) {
        opener.setAttribute('aria-expanded', String(state));
      }
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener instanceof HTMLElement ? opener : document.activeElement;
      this.classList.add('is-open');
      theme.overlays.push(this);
      theme.scrollLock.lock();
      this.setExpanded(true);
      window.setTimeout(() => {
        if (this.isOpen) theme.a11y.focusInto(this.panel, this.focusTarget());
      }, 50);
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      theme.overlays = theme.overlays.filter((overlay) => overlay !== this);
      theme.scrollLock.unlock();
      theme.a11y.releaseFocus();
      this.setExpanded(false);
      const restore = this.opener && document.body.contains(this.opener) && this.opener.getClientRects().length ? this.opener : null;
      const previous = theme.overlays[theme.overlays.length - 1];
      if (previous) {
        theme.a11y.trapFocus(previous.panel, restore || previous.panel);
      } else if (restore) {
        restore.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }
  }

  theme.OverlayElement = OverlayElement;

  define('modal-dialog', class ModalDialog extends OverlayElement {});

  define(
    'menu-drawer',
    class MenuDrawer extends OverlayElement {
      connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', (event) => {
          if (event.target.closest('a[href]')) this.close();
        });
      }
    }
  );

  /* ---------------------------------------------------------------------
     Search modal + predictive search (combobox)
     --------------------------------------------------------------------- */
  class SearchModal extends OverlayElement {
    connectedCallback() {
      super.connectedCallback();
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      if (!this.input || !this.results || this.dataset.predictive !== 'true') return;
      this.cache = new Map();
      this.input.addEventListener('input', theme.utils.debounce(() => this.search(), 220));
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    focusTarget() {
      return this.input || this.panel;
    }

    async search() {
      const term = this.input.value.trim();
      if (!term) {
        this.setResults('');
        return;
      }
      if (this.cache.has(term)) {
        this.setResults(this.cache.get(term));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const doc = theme.utils.parseHTML(await response.text());
        const inner = $('[data-predictive-inner]', doc);
        const markup = inner ? inner.outerHTML : '';
        this.cache.set(term, markup);
        this.setResults(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.setResults('');
      }
    }

    setResults(markup) {
      this.results.innerHTML = markup;
      const expanded = Boolean(markup);
      this.results.hidden = !expanded;
      this.input.setAttribute('aria-expanded', String(expanded));
      this.input.removeAttribute('aria-activedescendant');
    }

    onKeydown(event) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const options = $$('[role="option"]', this.results);
      if (!options.length) return;
      const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
      if (event.key === 'Enter') {
        if (current > -1) {
          const link = $('a', options[current]);
          if (link) {
            event.preventDefault();
            window.location.href = link.href;
          }
        }
        return;
      }
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }

  define('search-modal', SearchModal);

  /* ---------------------------------------------------------------------
     Cart API
     --------------------------------------------------------------------- */
  const swapPreservingFocus = (current, fresh, host) => {
    const active = document.activeElement;
    const hadFocus = active && current.contains(active);
    const focusId = hadFocus ? active.id : null;
    current.replaceWith(fresh);
    if (!hadFocus) return;
    const target = (focusId && document.getElementById(focusId)) || host.querySelector('[role="dialog"]') || host;
    if (target.focus) target.focus({ preventScroll: true });
  };

  theme.cartApi = {
    sectionIds() {
      const ids = [];
      if ($('cart-drawer')) ids.push('cart-drawer');
      const page = $('cart-items[data-section-id]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },

    async request(url, options) {
      const response = await fetch(url, options);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }
      return data;
    },

    jsonOptions(body) {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      };
    },

    async add(payload) {
      const sections = this.sectionIds();
      let options;
      if (payload instanceof FormData) {
        if (sections.length) {
          payload.append('sections', sections.join(','));
          payload.append('sections_url', window.location.pathname);
        }
        options = {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: payload,
        };
      } else {
        const body = Object.assign({}, payload);
        if (sections.length) {
          body.sections = sections.join(',');
          body.sections_url = window.location.pathname;
        }
        options = this.jsonOptions(body);
      }
      const data = await this.request(`${theme.routes.cartAdd}.js`, options);
      const cart = await this.get();
      this.publish(cart, data.sections);
      return data;
    },

    async change(line, quantity) {
      const sections = this.sectionIds();
      const body = { line, quantity };
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }
      const cart = await this.request(`${theme.routes.cartChange}.js`, this.jsonOptions(body));
      this.publish(cart, cart.sections);
      return cart;
    },

    note(note) {
      return this.request(`${theme.routes.cartUpdate}.js`, this.jsonOptions({ note }));
    },

    async get() {
      const response = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    },

    publish(cart, sections) {
      if (sections) this.renderSections(sections);
      this.updateCounts(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    },

    renderSections(sections) {
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const doc = theme.utils.parseHTML(html);
        if (id === 'cart-drawer') {
          const drawer = $('cart-drawer');
          const fresh = $('[data-cart-drawer-inner]', doc);
          const current = drawer && $('[data-cart-drawer-inner]', drawer);
          if (current && fresh) swapPreservingFocus(current, fresh, drawer);
          return;
        }
        const host = $(`cart-items[data-section-id="${id}"]`);
        const fresh = $('[data-cart-page]', doc);
        const current = host && $('[data-cart-page]', host);
        if (current && fresh) swapPreservingFocus(current, fresh, host);
      });
    },

    updateCounts(cart) {
      if (!cart || typeof cart.item_count !== 'number') return;
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
      $$('[data-cart-count-label]').forEach((el) => {
        el.textContent = (theme.strings.cartCountLabel || '').replace('[count]', cart.item_count);
      });
    },

    afterAdd(title, opener) {
      const type = theme.cart.type;
      if (type === 'page') {
        window.location.href = theme.routes.cart;
        return;
      }
      const drawer = $('cart-drawer');
      if (type === 'drawer' && drawer) {
        drawer.open(opener);
        return;
      }
      theme.toast((theme.cart.toastText || '').replace('[title]', title || ''));
    },
  };

  /* Quantity + remove handling shared by the drawer and the cart page. */
  const bindCartLines = (host) => {
    const timers = {};
    const update = async (line, quantity) => {
      host.classList.add('is-loading');
      try {
        await theme.cartApi.change(Number(line), Math.max(0, Number(quantity) || 0));
      } catch (error) {
        theme.toast(error.message);
      } finally {
        host.classList.remove('is-loading');
      }
    };
    host.addEventListener('change', (event) => {
      const input = event.target.closest('input[data-line]');
      if (input) {
        clearTimeout(timers[input.dataset.line]);
        timers[input.dataset.line] = setTimeout(() => update(input.dataset.line, input.value), 350);
        return;
      }
      const note = event.target.closest('[data-cart-note]');
      if (note) theme.cartApi.note(note.value).catch(() => {});
    });
    host.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      update(remove.dataset.cartRemove, 0);
    });
  };

  define(
    'cart-drawer',
    class CartDrawer extends OverlayElement {
      connectedCallback() {
        super.connectedCallback();
        if (this.linesBound) return;
        this.linesBound = true;
        bindCartLines(this);
      }
    }
  );

  define(
    'cart-items',
    class CartItems extends HTMLElement {
      connectedCallback() {
        if (this.linesBound) return;
        this.linesBound = true;
        bindCartLines(this);
      }
    }
  );

  /* ---------------------------------------------------------------------
     Product forms (cards, upsells, buy box)
     --------------------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.form = $('form', this);
      if (!this.form) return;
      this.bound = true;
      this.buyNow = false;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    productTitle() {
      const card = this.closest('[data-product-card]');
      if (card) return ($('.card__link', card) || {}).textContent || '';
      const upsell = this.closest('.upsell-item');
      if (upsell) return ($('.upsell-item__title', upsell) || {}).textContent || '';
      const title = $('.pdp__title') || $('.editorial__heading');
      return title ? title.textContent : '';
    }

    async onSubmit(event) {
      event.preventDefault();
      const submitter = event.submitter || $('[type="submit"]', this.form);
      if (!submitter || submitter.disabled || submitter.classList.contains('is-loading')) return;

      const formData = new FormData(this.form);
      if (submitter.name && submitter.value) formData.set(submitter.name, submitter.value);
      if (!formData.get('id')) return;

      const errorEl = $('[data-form-error]', this);
      if (errorEl) errorEl.hidden = true;
      const buyNow = this.buyNow;
      this.buyNow = false;
      const busyButton = buyNow ? $('[data-buy-now]', this.closest('product-info') || document) || submitter : submitter;
      busyButton.classList.add('is-loading');
      busyButton.setAttribute('aria-busy', 'true');

      const quickAdd = this.closest('quick-add');
      const opener = quickAdd && quickAdd.toggleButton ? quickAdd.toggleButton : submitter;

      try {
        await theme.cartApi.add(formData);
        this.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
        if (quickAdd) quickAdd.close();
        if (buyNow) {
          window.location.href = `${theme.routes.root.replace(/\/$/, '')}/checkout`;
          return;
        }
        theme.cartApi.afterAdd(this.productTitle().trim(), opener);
      } catch (error) {
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        } else {
          theme.toast(error.message);
        }
      } finally {
        busyButton.classList.remove('is-loading');
        busyButton.removeAttribute('aria-busy');
      }
    }
  }

  define('product-form', ProductForm);

  class QuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.toggleButton = $('[data-quick-add-toggle]', this);
      this.panel = $('.quick-add__panel', this);
      if (!this.toggleButton || !this.panel) return;
      this.bound = true;
      this.onOutside = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.toggleButton.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-quick-add-close]')) {
          this.close();
          this.toggleButton.focus();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.panel.hidden) {
          event.stopPropagation();
          this.close();
          this.toggleButton.focus();
        }
      });
    }

    open() {
      $$('quick-add').forEach((other) => {
        if (other !== this && typeof other.close === 'function') other.close();
      });
      this.panel.hidden = false;
      this.toggleButton.setAttribute('aria-expanded', 'true');
      const first = $('.quick-add__option:not([disabled])', this.panel);
      if (first) first.focus();
      window.setTimeout(() => document.addEventListener('click', this.onOutside), 0);
    }

    close() {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      this.toggleButton.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onOutside);
    }
  }

  define('quick-add', QuickAdd);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.input = $('input', this);
      if (!this.input) return;
      this.bound = true;
      this.minus = $('button[name="minus"]', this);
      this.plus = $('button[name="plus"]', this);
      [this.minus, this.plus].forEach((button) => {
        if (!button) return;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const previous = this.input.value;
          if (button.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.input.addEventListener('change', () => this.sync());
      this.sync();
    }

    sync() {
      const value = parseInt(this.input.value, 10) || 0;
      const min = parseInt(this.input.min, 10) || 0;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      if (value < min) this.input.value = min;
      if (this.minus) this.minus.disabled = value <= min;
      if (this.plus) this.plus.disabled = max !== null && value >= max;
    }
  }

  define('quantity-input', QuantityInput);

  /* ---------------------------------------------------------------------
     Free shipping progress bar
     --------------------------------------------------------------------- */
  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.render(Number(this.dataset.subtotal) || 0);
      this.onCart = (event) => this.render(event.detail.cart.total_price);
      document.addEventListener('cart:updated', this.onCart);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCart);
    }

    render(subtotal) {
      const threshold = Math.round((Number(this.dataset.threshold) || 0) * theme.currencyRate());
      if (!threshold) return;
      const remaining = Math.max(threshold - subtotal, 0);
      const percent = Math.min(100, Math.round((subtotal / threshold) * 100));
      const unlocked = remaining === 0;
      const message = $('[data-fsb-message]', this);
      const fill = $('[data-fsb-fill]', this);
      const track = $('[data-fsb-track]', this);
      this.classList.toggle('is-unlocked', unlocked);
      if (message) {
        message.textContent = '';
        if (unlocked) {
          message.insertAdjacentHTML('afterbegin', '<svg class="icon icon-check icon--sm" aria-hidden="true" focusable="false"><use href="#icon-check"></use></svg>');
          message.append(document.createTextNode(` ${theme.freeShipping.success || ''}`));
        } else {
          message.append(document.createTextNode((theme.freeShipping.progress || '').replace('[amount]', theme.formatMoneyShort(remaining))));
        }
      }
      if (fill) fill.style.setProperty('--progress', `${percent}%`);
      if (track) track.setAttribute('aria-valuenow', String(percent));
    }
  }

  define('free-shipping-bar', FreeShippingBar);

  /* ---------------------------------------------------------------------
     Header: sticky / transparent / hide-on-scroll
     --------------------------------------------------------------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header');
      this.mode = this.dataset.sticky;
      this.lastY = window.scrollY;
      this.setHeight();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.setHeight());
        this.resizeObserver.observe(this);
      }
      this.onScroll = theme.utils.onFrame(() => this.update());
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    setHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    }

    update() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 10);
      if (this.mode === 'scroll-up' && this.section) {
        const menuOpen = Boolean($('[aria-expanded="true"]', this));
        const hide = y > this.lastY && y > this.offsetHeight * 2 && !menuOpen;
        this.section.classList.toggle('is-hidden', hide);
      }
      this.lastY = y;
    }
  }

  define('sticky-header', StickyHeader);

  /* Disclosure navigation with mega menus (APG disclosure pattern). */
  class HeaderNav extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-nav-item]', this);
      const canHover = window.matchMedia('(hover: hover)').matches;
      this.items.forEach((item) => {
        const button = $('button[aria-controls]', item);
        const panel = button && document.getElementById(button.getAttribute('aria-controls'));
        if (!button || !panel) return;
        button.addEventListener('click', () => this.toggle(item, button.getAttribute('aria-expanded') !== 'true'));
        if (canHover) {
          item.addEventListener('mouseenter', () => {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => this.toggle(item, true), 60);
          });
          item.addEventListener('mouseleave', () => {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => this.toggle(item, false), 160);
          });
        }
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && button.getAttribute('aria-expanded') === 'true') {
            event.stopPropagation();
            this.toggle(item, false);
            button.focus();
          }
          if (event.key === 'ArrowDown' && event.target === button) {
            event.preventDefault();
            this.toggle(item, true);
            const firstLink = $('a', panel);
            if (firstLink) firstLink.focus();
          }
        });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.toggle(item, false);
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const tops = $$('.nav > .nav__item > .nav__link', this);
        const index = tops.indexOf(document.activeElement);
        if (index === -1) return;
        event.preventDefault();
        const step = event.key === 'ArrowRight' ? 1 : -1;
        tops[(index + step + tops.length) % tops.length].focus();
      });

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    toggle(item, open) {
      const button = $('button[aria-controls]', item);
      const panel = button && document.getElementById(button.getAttribute('aria-controls'));
      if (!button || !panel) return;
      if (open) this.closeAll(item);
      button.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      item.classList.toggle('is-open', open);
    }

    closeAll(except) {
      this.items.forEach((item) => {
        if (item !== except) this.toggle(item, false);
      });
    }
  }

  define('header-nav', HeaderNav);

  /* ---------------------------------------------------------------------
     Announcement bar rotation + countdown
     --------------------------------------------------------------------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2) return;
      this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.paused = theme.utils.reducedMotion();
      this.hovered = false;
      this.desktop = window.matchMedia('(min-width: 1024px)');
      this.pauseButton = $('[data-announcement-pause]', this);
      if (this.pauseButton) {
        this.pauseButton.setAttribute('aria-pressed', String(this.paused));
        this.pauseButton.addEventListener('click', () => {
          this.paused = !this.paused;
          this.pauseButton.setAttribute('aria-pressed', String(this.paused));
          const use = $('use', this.pauseButton);
          if (use) use.setAttribute('href', this.paused ? '#icon-play' : '#icon-pause');
        });
      }
      this.addEventListener('mouseenter', () => (this.hovered = true));
      this.addEventListener('mouseleave', () => (this.hovered = false));
      this.addEventListener('focusin', () => (this.hovered = true));
      this.addEventListener('focusout', () => (this.hovered = false));
      this.timer = window.setInterval(() => this.tick(), (Number(this.dataset.speed) || 4) * 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const inline = this.desktop.matches && this.dataset.desktop === 'inline';
      if (inline || this.paused || this.hovered || document.hidden) return;
      const visible = this.items.filter((item) => !item.hidden);
      if (visible.length < 2) return;
      const current = this.items[this.index];
      if (current) current.classList.remove('is-active');
      let next = this.index;
      do {
        next = (next + 1) % this.items.length;
      } while (this.items[next].hidden);
      this.index = next;
      this.items[next].classList.add('is-active');
    }
  }

  define('announcement-bar', AnnouncementBar);

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.clock = $('[data-countdown-clock]', this);
      this.label = $('[data-countdown-label]', this);
      this.end = new Date(String(this.dataset.end || '').trim().replace(' ', 'T')).getTime();
      if (Number.isNaN(this.end)) {
        this.hidden = true;
        return;
      }
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const remaining = this.end - Date.now();
      if (remaining <= 0) {
        window.clearInterval(this.timer);
        if (this.dataset.hideExpired === 'true') {
          const item = this.closest('.announcement__item');
          (item || this).hidden = true;
        } else {
          if (this.label) this.label.textContent = this.dataset.expired || '';
          if (this.clock) this.clock.textContent = '';
        }
        return;
      }
      const pad = (value) => String(value).padStart(2, '0');
      const days = Math.floor(remaining / 864e5);
      const hours = Math.floor((remaining % 864e5) / 36e5);
      const minutes = Math.floor((remaining % 36e5) / 6e4);
      const seconds = Math.floor((remaining % 6e4) / 1e3);
      if (this.clock) {
        this.clock.textContent = days > 0 ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }
    }
  }

  define('countdown-timer', CountdownTimer);

  /* ---------------------------------------------------------------------
     Swipe carousel (scroll-snap) with prev/next
     --------------------------------------------------------------------- */
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-carousel-track]', this);
      if (!this.track) return;
      this.prev = $('[data-carousel-prev]', this);
      this.next = $('[data-carousel-next]', this);
      this.nav = $('.carousel-nav', this);
      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.onScroll = theme.utils.debounce(() => this.update(), 60);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    step(direction) {
      const item = this.track.firstElementChild;
      if (!item) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const width = item.getBoundingClientRect().width + gap;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / width));
      this.track.scrollBy({ left: direction * width * perView, behavior: theme.utils.reducedMotion() ? 'auto' : 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      if (this.nav) this.nav.hidden = max <= 0;
    }
  }

  define('carousel-slider', CarouselSlider);

  /* ---------------------------------------------------------------------
     Email capture + exit-intent popups
     --------------------------------------------------------------------- */
  class EmailPopup extends OverlayElement {
    connectedCallback() {
      super.connectedCallback();
      if (this.popupReady) return;
      this.popupReady = true;
      this.key = `hw-popup-${this.dataset.trigger}`;
      const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';

      if (posted && window.location.hash === `#${this.dataset.formId}`) {
        this.open();
        theme.utils.storage.set(this.key, { until: Date.now() + 365 * 864e5 });
        return;
      }
      if (this.dataset.preview === 'true') {
        this.open();
        return;
      }
      if (theme.designMode || theme.template === 'cart' || this.suppressed()) return;

      this.addEventListener('overlay:close', () => this.suppress());
      if (this.dataset.trigger === 'exit') {
        this.armExitIntent();
      } else {
        window.setTimeout(() => this.maybeOpen(), (Number(this.dataset.delay) || 0) * 1000);
      }
    }

    suppressed() {
      if (theme.utils.session.get(this.key)) return true;
      const record = theme.utils.storage.get(this.key);
      return Boolean(record && record.until > Date.now());
    }

    suppress() {
      const days = Number(this.dataset.frequency) || 0;
      theme.utils.session.set(this.key, '1');
      theme.utils.storage.set(this.key, { until: Date.now() + days * 864e5 });
    }

    maybeOpen() {
      if (this.suppressed()) return;
      if (theme.overlays.length) {
        window.setTimeout(() => this.maybeOpen(), 4000);
        return;
      }
      this.open();
    }

    armExitIntent() {
      window.setTimeout(() => {
        const cleanup = () => {
          document.removeEventListener('mouseout', onLeave);
          window.removeEventListener('scroll', onScroll);
        };
        const onLeave = (event) => {
          if (event.clientY <= 0 && !event.relatedTarget) {
            cleanup();
            this.maybeOpen();
          }
        };
        let lastY = window.scrollY;
        let lastTime = Date.now();
        const onScroll = () => {
          const now = Date.now();
          const velocity = (window.scrollY - lastY) / Math.max(now - lastTime, 1);
          if (window.scrollY > 600 && velocity < -1.5) {
            cleanup();
            this.maybeOpen();
          }
          lastY = window.scrollY;
          lastTime = now;
        };
        document.addEventListener('mouseout', onLeave);
        if (window.matchMedia('(hover: none)').matches) window.addEventListener('scroll', onScroll, { passive: true });
      }, (Number(this.dataset.delay) || 0) * 1000);
    }
  }

  define('email-popup', EmailPopup);

  /* ---------------------------------------------------------------------
     Social proof toast (illustrative loop)
     --------------------------------------------------------------------- */
  class SocialProofToast extends HTMLElement {
    connectedCallback() {
      const source = $('[data-sp-data]', this);
      if (!source || theme.utils.session.get('hw-sp-dismissed')) return;
      try {
        this.data = JSON.parse(source.textContent);
      } catch (error) {
        return;
      }
      if (!this.data.products || !this.data.products.length) return;
      this.interval = (Number(this.dataset.interval) || 20) * 1000;
      this.duration = (Number(this.dataset.duration) || 5) * 1000;
      this.max = Number(this.dataset.max) || 5;
      this.count = Number(theme.utils.session.get('hw-sp-count')) || 0;
      this.link = $('[data-sp-link]', this);
      this.image = $('[data-sp-img]', this);
      this.message = $('[data-sp-message]', this);
      this.title = $('[data-sp-title]', this);
      this.time = $('[data-sp-time]', this);
      const close = $('[data-sp-close]', this);
      if (close) {
        close.addEventListener('click', () => {
          this.hide();
          theme.utils.session.set('hw-sp-dismissed', '1');
          clearTimeout(this.cycleTimer);
        });
      }
      this.addEventListener('mouseenter', () => clearTimeout(this.hideTimer));
      this.addEventListener('mouseleave', () => {
        this.hideTimer = setTimeout(() => this.hide(), 1500);
      });
      this.cycleTimer = setTimeout(() => this.show(), Math.min(this.interval, 8000));
    }

    disconnectedCallback() {
      clearTimeout(this.cycleTimer);
      clearTimeout(this.hideTimer);
    }

    pick(list) {
      return list[Math.floor(Math.random() * list.length)];
    }

    show() {
      if (this.count >= this.max) return;
      if (document.hidden || theme.overlays.length) {
        this.cycleTimer = setTimeout(() => this.show(), this.interval);
        return;
      }
      const product = this.pick(this.data.products);
      const city = this.data.cities && this.data.cities.length ? this.pick(this.data.cities) : '';
      const minutes = 2 + Math.floor(Math.random() * 50);
      this.link.href = product.url;
      if (product.image) {
        this.image.src = product.image;
        this.image.hidden = false;
      } else {
        this.image.hidden = true;
      }
      this.message.textContent = (this.data.template || '').replace('[city]', city);
      this.title.textContent = product.title;
      this.time.textContent = (this.data.time || '').replace('[minutes]', minutes);
      this.classList.add('is-visible');
      this.count += 1;
      theme.utils.session.set('hw-sp-count', String(this.count));
      this.hideTimer = setTimeout(() => this.hide(), this.duration);
      this.cycleTimer = setTimeout(() => this.show(), this.duration + this.interval);
    }

    hide() {
      this.classList.remove('is-visible');
    }
  }

  define('social-proof-toast', SocialProofToast);

  /* ---------------------------------------------------------------------
     Cookie consent (Shopify Customer Privacy API), privacy notice, age gate
     --------------------------------------------------------------------- */
  class CookieBanner extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const choice = event.target.closest('[data-consent]');
        if (choice) this.decide(choice.dataset.consent === 'accept');
      });
      const init = () => this.evaluate();
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], init);
      } else {
        init();
      }
    }

    evaluate() {
      const stored = theme.utils.storage.get('hw-consent');
      const api = window.Shopify && window.Shopify.customerPrivacy;
      let undecided = !stored;
      if (api && typeof api.currentVisitorConsent === 'function') {
        const consent = api.currentVisitorConsent() || {};
        undecided = undecided && Object.values(consent).every((value) => value === '' || value === 'no_interaction');
      } else if (api && typeof api.getTrackingConsent === 'function') {
        undecided = undecided && api.getTrackingConsent() === 'no_interaction';
      }
      this.hidden = !(undecided || theme.designMode);
    }

    decide(accepted) {
      theme.utils.storage.set('hw-consent', { accepted, at: Date.now() });
      let finished = false;
      const done = () => {
        if (finished) return;
        finished = true;
        this.hidden = true;
        document.dispatchEvent(new CustomEvent('consent:changed', { detail: { accepted } }));
      };
      const api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.setTrackingConsent === 'function') {
        try {
          api.setTrackingConsent({ analytics: accepted, marketing: accepted, preferences: accepted, sale_of_data: accepted }, done);
        } catch (error) {
          try {
            api.setTrackingConsent(accepted, done);
          } catch (fallbackError) {
            done();
          }
        }
        window.setTimeout(done, 1500);
      } else {
        done();
      }
    }
  }

  define('cookie-banner', CookieBanner);

  class PrivacyBanner extends HTMLElement {
    connectedCallback() {
      const version = this.dataset.version || '1';
      this.hidden = theme.utils.storage.get('hw-privacy') === version && !theme.designMode;
      const dismiss = $('[data-privacy-dismiss]', this);
      if (dismiss) {
        dismiss.addEventListener('click', () => {
          theme.utils.storage.set('hw-privacy', version);
          this.hidden = true;
        });
      }
    }
  }

  define('privacy-banner', PrivacyBanner);

  class AgeVerification extends OverlayElement {
    connectedCallback() {
      this.dismissible = false;
      super.connectedCallback();
      if (this.ageReady) return;
      this.ageReady = true;
      const record = theme.utils.storage.get('hw-age');
      if ((record && record.until > Date.now()) || theme.utils.session.get('hw-age')) {
        this.remove();
        return;
      }
      const confirm = $('[data-age-confirm]', this);
      const decline = $('[data-age-decline]', this);
      if (confirm) {
        confirm.addEventListener('click', () => {
          const days = Number(this.dataset.remember) || 0;
          if (days > 0) theme.utils.storage.set('hw-age', { until: Date.now() + days * 864e5 });
          else theme.utils.session.set('hw-age', '1');
          this.close();
          this.remove();
        });
      }
      if (decline) {
        decline.addEventListener('click', () => {
          if (this.dataset.declineUrl) {
            window.location.href = this.dataset.declineUrl;
            return;
          }
          const question = $('[data-age-question]', this);
          const declined = $('[data-age-declined]', this);
          if (question) question.hidden = true;
          if (declined) {
            declined.hidden = false;
            declined.setAttribute('tabindex', '-1');
            declined.focus();
          }
        });
      }
      this.open();
    }

    focusTarget() {
      return $('[data-age-confirm]', this) || this.panel;
    }
  }

  define('age-verification', AgeVerification);

  /* ---------------------------------------------------------------------
     Back to top
     --------------------------------------------------------------------- */
  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.button = $('button', this);
      this.offset = Number(this.dataset.offset) || 800;
      this.onScroll = theme.utils.onFrame(() => {
        const visible = window.scrollY > this.offset;
        this.classList.toggle('is-visible', visible);
        if (this.button) this.button.tabIndex = visible ? 0 : -1;
      });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.button) {
        this.button.addEventListener('click', () => {
          window.scrollTo({ top: 0, behavior: theme.utils.reducedMotion() ? 'auto' : 'smooth' });
          const main = document.getElementById('MainContent');
          if (main) main.focus({ preventScroll: true });
        });
      }
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  define('back-to-top', BackToTop);

  /* ---------------------------------------------------------------------
     Customer address country/province selects
     --------------------------------------------------------------------- */
  const initCountrySelects = (scope = document) => {
    $$('[data-country-select]', scope).forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      const target = document.getElementById(select.dataset.provinceTarget);
      const wrap = document.querySelector(`[data-province-wrap="${select.dataset.provinceTarget}"]`);
      const initial = select.dataset.default;
      if (initial) {
        const match = Array.from(select.options).find((option) => option.value === initial || option.textContent.trim() === initial);
        if (match) select.value = match.value;
      }
      const update = () => {
        if (!target) return;
        const option = select.options[select.selectedIndex];
        let provinces = [];
        try {
          provinces = option ? JSON.parse(option.dataset.provinces || '[]') : [];
        } catch (error) {
          provinces = [];
        }
        target.innerHTML = '';
        if (wrap) wrap.hidden = !provinces.length;
        provinces.forEach(([value, label]) => target.add(new Option(label, value)));
        if (target.dataset.default) target.value = target.dataset.default;
      };
      select.addEventListener('change', () => {
        if (target) target.dataset.default = '';
        update();
      });
      update();
    });
  };

  /* ---------------------------------------------------------------------
     Delegated triggers
     --------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const cartTrigger = event.target.closest('[data-open-cart]');
    if (cartTrigger && theme.cart.type === 'drawer' && theme.template !== 'cart') {
      const drawer = $('cart-drawer');
      if (drawer) {
        event.preventDefault();
        drawer.open(cartTrigger);
        return;
      }
    }

    const searchTrigger = event.target.closest('[data-open-search]');
    if (searchTrigger) {
      const modal = $('search-modal');
      if (modal) {
        event.preventDefault();
        modal.open(searchTrigger);
        return;
      }
    }

    const menuTrigger = event.target.closest('[data-open-menu]');
    if (menuTrigger) {
      const drawer = $('menu-drawer');
      if (drawer) {
        event.preventDefault();
        drawer.open(menuTrigger);
        return;
      }
    }

    const modalTrigger = event.target.closest('[data-modal-open]');
    if (modalTrigger) {
      const modal = document.getElementById(modalTrigger.dataset.modalOpen);
      if (modal && typeof modal.open === 'function') {
        event.preventDefault();
        modal.open(modalTrigger);
        return;
      }
    }

    const copyTrigger = event.target.closest('[data-copy]');
    if (copyTrigger && navigator.clipboard) {
      navigator.clipboard.writeText(copyTrigger.dataset.copy).then(() => theme.toast(theme.strings.copied));
      return;
    }

    if (event.target.closest('[data-print]')) {
      window.print();
      return;
    }

    const customerToggle = event.target.closest('[data-customer-toggle]');
    if (customerToggle) {
      event.preventDefault();
      const showRecover = customerToggle.dataset.customerToggle === 'recover';
      const login = document.getElementById('CustomerLogin');
      const recover = document.getElementById('RecoverPassword');
      if (login && recover) {
        login.hidden = showRecover;
        recover.hidden = !showRecover;
        const field = $('input:not([type="hidden"])', showRecover ? recover : login);
        if (field) field.focus();
      }
      return;
    }

    const addressToggle = event.target.closest('[data-address-toggle]');
    if (addressToggle) {
      const target = document.getElementById(addressToggle.getAttribute('aria-controls'));
      if (!target) return;
      target.hidden = !target.hidden;
      $$(`[data-address-toggle][aria-controls="${target.id}"]`).forEach((button) => button.setAttribute('aria-expanded', String(!target.hidden)));
      if (!target.hidden) {
        const field = $('input:not([type="hidden"]), select', target);
        if (field) field.focus();
      }
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-confirm]');
    if (form && !window.confirm(form.dataset.confirm)) event.preventDefault();
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-autosubmit]') && event.target.form) event.target.form.submit();
  });

  /* Theme Editor support */
  document.addEventListener('shopify:section:load', (event) => initCountrySelects(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-carousel-track] > *');
    if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    const mega = event.target.closest('.mega');
    if (mega) {
      const item = mega.closest('[data-nav-item]');
      const nav = item && item.closest('header-nav');
      if (nav && typeof nav.toggle === 'function') nav.toggle(item, true);
    }
  });

  /* Init */
  initCountrySelects();
  if (/recover/i.test(window.location.hash)) {
    const login = document.getElementById('CustomerLogin');
    const recover = document.getElementById('RecoverPassword');
    if (login && recover) {
      login.hidden = true;
      recover.hidden = false;
    }
  }
  const qr = document.getElementById('QrCode');
  if (qr && window.QRCode) {
    new window.QRCode(qr, { text: qr.dataset.identifier, width: 160, height: 160 });
  }
  if (window.Shopify && window.Shopify.currency) {
    document.dispatchEvent(new CustomEvent('theme:ready'));
  }
})();
